/**
 * `R` — rectangle (§9.3). A four-point ring, clockwise in screen space.
 * `Shift` keeps it square in percent space.
 */
import { rectRing } from '@/geo/polygon'

import { idle, none, withState, type ToolHandler, type ToolResult } from './types'

export const MIN_RECT_SIZE_PCT = 0.5

export const rectTool: ToolHandler = {
  id: 'rect',
  shortcut: 'r',
  icon: 'Square',
  label: 'Box',
  hint: 'Drag a box around something square-ish — a door, a rug, a window.',
  cursor: 'crosshair',

  reduce(machine, ev, cfg): ToolResult | null {
    const st = machine.state

    if (ev.t === 'pointerdown' && ev.button === 0) {
      return withState('rect', { t: 'rect.dragging', from: ev.at, to: ev.at })
    }

    if (ev.t === 'pointermove') {
      if (st.t !== 'rect.dragging') return none(machine)
      return withState('rect', { ...st, to: ev.shift ? square(st.from, ev.at) : ev.at })
    }

    if (ev.t === 'pointerup') {
      if (st.t !== 'rect.dragging') return none(machine)
      const to = ev.shift ? square(st.from, ev.at) : ev.at
      if (Math.abs(to[0] - st.from[0]) < MIN_RECT_SIZE_PCT || Math.abs(to[1] - st.from[1]) < MIN_RECT_SIZE_PCT) return idle('rect')
      return idle('rect', [{ k: 'beginGesture' }, { k: 'createRegion', ring: rectRing(st.from, to), kind: cfg.activeKind.region }, { k: 'endGesture' }])
    }

    if (ev.t === 'cancel' || ev.t === 'pointercancel') return idle('rect')

    return null
  },
}

function square(from: [number, number], to: [number, number]): [number, number] {
  const dx = to[0] - from[0]
  const dy = to[1] - from[1]
  const s = Math.max(Math.abs(dx), Math.abs(dy))
  return [from[0] + Math.sign(dx || 1) * s, from[1] + Math.sign(dy || 1) * s]
}
